import { EventTypes } from '@candulabs/craft';
import { Node as CraftNode } from '@candulabs/craft/dist/core/nodes/types';
import React, { MutableRefObject, useEffect, useRef } from 'react';

import { getNodeId, TOUR_STEP } from '@candu-ai/styleguide/selectors';
import { toCssSelector } from '@candu-ai/stylesheet/utils';
import { useEventing } from 'commons/eventing';
import { Flags, useFlags, useOutsideClick } from 'commons/hooks';
import Placeholder from 'craft/components/Placeholder';
import { createVariantsSelector } from 'craft/components/SelectedToolbar/VariantsSelector';
import { useActiveTooltip } from 'craft/components/Tooltip';
import { VariantsSelectorToolbox } from 'craft/components/VariantsSelectorToolbox';
import { BuiltInComponents } from 'craft/constants';
import { VIEWPORT_CONTENT_ID } from 'craft/containers/Viewport';
import {
  useEditor,
  useMultiCssSelector,
  useNodeContext,
  useNodeCustom,
  useSelection,
  useTooltipPlacement,
} from 'craft/hooks';
import { HotspotTooltip } from 'craft/resolvers/Hotspot/HotspotTooltip';
import { TooltipPosition as Position } from 'craft/resolvers/Hotspot/types';
import { useTooltipPosition } from 'craft/resolvers/Hotspot/utils/useTooltipPosition';
import { usePlacementMode, useToolboxState, useToolboxTab } from 'craft/state';
import { CssSelectorId, Resolver, ToolboxComponentSections } from 'craft/types';
import { hasDescendants } from 'craft/utils';
import { getSelectorFromIframe } from 'craft/utils/snapshot-iframe';

import { Backdrop } from './Backdrop';
import { TooltipPosition } from './toolbox/TooltipPosition';
import { TourArrowSize } from './toolbox/TourArrowSize';
import { TourBackdrop } from './toolbox/TourBackdrop';
import { TourDismiss } from './toolbox/TourDismiss';
import { TourProgress } from './toolbox/TourProgress';

const FREEZE_SELECTORS = {};
Object.freeze(FREEZE_SELECTORS);

export const TourStep: Resolver = ({
  children,
  hasDismiss,
  hasBackdrop,
  variant = 'base',
}: any) => {
  const { selection } = useSelection();
  const { actions } = useEditor();
  const { id } = useNodeContext();
  const { [Flags.TOUR_STEP_BACKDROP]: tourStepBackdrop } = useFlags();
  const { placement: tourStepPlacement } = useTooltipPlacement(id);
  const activeTourStep = useActiveTooltip();

  const tooltipPosition = useNodeCustom(id)?.custom?.tooltipPosition || Position.Auto;
  const customCssSelectors = useNodeCustom(id)?.cssSelectors || FREEZE_SELECTORS;
  const { css: tooltipCss } = useMultiCssSelector([id], CssSelectorId.TourStepWrapper);

  const { track } = useEventing();
  const { minimizeToolboxOnSnapshotClick } = useToolboxState();

  const arrowClassname = toCssSelector(TOUR_STEP.Arrow, {
    variant: { variant },
    output: 'html',
  });

  const wrapperClassname = toCssSelector(TOUR_STEP.Wrapper, {
    variant: { variant },
    output: 'html',
  });

  const containerClassname = toCssSelector(TOUR_STEP.Container, {
    variant: { variant },
    output: 'html',
  });

  const bodyClassname = toCssSelector(TOUR_STEP.Body, {
    variant: { variant },
    output: 'html',
  });

  const { previewSelector } = usePlacementMode();
  const { forcePlacementsTab } = useToolboxTab();

  const isStepActive = activeTourStep?.id === id;
  const tourStepSelector =
    forcePlacementsTab && isStepActive ? previewSelector : tourStepPlacement?.selector;

  const placementSelectorEl = getSelectorFromIframe(tourStepSelector);

  const isStepVisible = forcePlacementsTab ? isStepActive && !!placementSelectorEl : true;

  const beaconRef = useRef<Element | undefined>(placementSelectorEl ?? undefined);

  const { refs, css, recalculate } = useTooltipPosition({
    // @ts-expect-error
    beaconRef,
    tooltipPosition,
    isActive: isStepActive,
    selector: tourStepSelector,
  });

  useEffect(() => {
    recalculate();
  }, [
    activeTourStep?.id,
    refs.wrapper.current,
    tourStepPlacement?.selector,
    tooltipPosition,
    tooltipCss,
    customCssSelectors,
  ]);

  useEffect(() => {
    beaconRef.current = placementSelectorEl ?? undefined;
    recalculate();
  }, [tourStepSelector, placementSelectorEl]);

  // @ts-ignore
  useOutsideClick(refs.wrapper?.current, (e) => {
    // @ts-ignore
    const targetId = e?.target?.id;

    if (targetId !== VIEWPORT_CONTENT_ID && targetId !== 'tourBackdrop') {
      // only consider an outside click when it's triggered from inside the canvas
      return;
    }

    if (!refs.wrapper?.current?.id.includes(activeTourStep?.id || '')) {
      // if the outside click handler fired is tied to a different hotspot, ignore it
      return;
    }

    if (minimizeToolboxOnSnapshotClick()) {
      track('editor.dockedToolbox.snapshotClose');
    }

    // toggle Hotspot focus on click outside
    if (activeTourStep?.id && !selection.first) {
      actions.setEventNodes(EventTypes.selected, [activeTourStep.id]);
    } else {
      actions.setEventNodes(EventTypes.selected, []);
    }
  });

  if (!tourStepSelector || !isStepVisible) {
    return null;
  }

  // We need ref updates to have side effects, so we wrap the base ref with some extra logic
  const refHandler = (onRef: MutableRefObject<HTMLDivElement | undefined>) => (
    ref: HTMLDivElement,
  ) => {
    // extra paranoid to avoid recalculating if ref hasn't actually changed
    if (!ref || onRef.current === ref) {
      return;
    }
    // eslint-disable-next-line no-param-reassign
    onRef.current = ref;
    recalculate();
  };

  const hasChildren = hasDescendants(children);

  return (
    <div key={`tour-node-${id}`} id={getNodeId(id)}>
      {activeTourStep?.id === id && hasBackdrop && tourStepBackdrop && (
        <Backdrop variant={variant} tourStepSelector={tourStepSelector} />
      )}
      {activeTourStep?.id === id && (
        <HotspotTooltip
          key={`hotspot-tooltip-${id}`}
          nodeId={id}
          isDismissible={!!hasDismiss}
          wrapperRef={refHandler(refs.wrapper)}
          wrapperCss={css.wrapper}
          arrowRef={refHandler(refs.arrow)}
          arrowCss={css.arrow}
          classNameObj={{
            Body: bodyClassname,
            Arrow: arrowClassname,
            Wrapper: wrapperClassname,
            Container: containerClassname,
          }}
        >
          {hasChildren ? children : <Placeholder />}
        </HotspotTooltip>
      )}
    </div>
  );
};

TourStep.craft = {
  name: BuiltInComponents.TourStep,
  displayName: 'Tour Step',
  isCanvas: true,
  rules: {
    canDelete: (node: CraftNode, helpers) => {
      const isLastRemaining = helpers(node.id).getParent().descendants().length === 1;
      return !isLastRemaining;
    },
    canDrop: (target: CraftNode) => {
      return target.data.name === BuiltInComponents.Tour;
    },
    canDrag: () => false,
  },
};

TourStep.toolbox = {
  schema: {
    [ToolboxComponentSections.NameAndType]: {
      variantSelector: VariantsSelectorToolbox,
    },
    [ToolboxComponentSections.Layout]: {
      cssSelectorId: CssSelectorId.TourStepBody,
      blockedDisplays: ['none'],
    },
    [ToolboxComponentSections.Size]: {
      cssSelectorId: CssSelectorId.TourStepBody,
      hasOverflow: true,
      hasPercentageSize: false,
    },
    [ToolboxComponentSections.Spacing]: {
      cssSelectorId: CssSelectorId.TourStepBody,
      hasMargin: false,
    },
    [ToolboxComponentSections.Background]: {
      cssSelectorId: CssSelectorId.TourStepWrapper,
      hasBackgroundImage: false,
    },
    [ToolboxComponentSections.Border]: {
      cssSelectorId: CssSelectorId.TourStepWrapper,
    },
    [ToolboxComponentSections.Shadow]: {
      cssSelectorId: CssSelectorId.TourStepWrapper,
      isFilter: true,
    },
  },
  component: [TourProgress, TooltipPosition, TourArrowSize, TourBackdrop, TourDismiss],
};

TourStep.selectedToolbar = {
  customItems: [
    createVariantsSelector({
      componentName: BuiltInComponents.TourStep,
      getConditions: (props) =>
        (props.variant?.value
          ? { name: props.variant?.value, variant: props.variant?.value }
          : { name: 'base' }) as Record<string, string>,
      variantToProps: (variant) => ({
        variant: variant.conditions.variant,
      }),
    }),
  ],
};
